import { oneOf, setParam } from '../shared/useUrlState';

/**
 * The address of an Argo CD view.
 *
 * Follows upstream's scheme (argo-cd `ui/src/app/applications/…`): filters as
 * query parameters, and the selected application and resource each carried as
 * a single composite key rather than one parameter per field. Upstream's
 * `nodeKey()` joins group/kind/namespace/name with slashes; ours does the
 * same, with the instance prefixed for applications because this page spans
 * several Argo CD instances.
 */
export interface ArgoUrlState {
  tab: ArgoTab;
  /** Free-text name filter. */
  q: string;
  instance: string;
  sync: string;
  health: string;
  project: string;
  cluster: string;
  ns: string;
  autosync: string;
  /** Label selectors, comma-separated in the URL. */
  labels: string[];
  favoritesOnly: boolean;
  view: ArgoView;
  page: number;
  /** `instance/namespace/name`; empty when no application is open. */
  app: string;
  appTab: ArgoAppTab;
  /** `group/kind/namespace/name`; empty when no resource is open. */
  res: string;
  resTab: ArgoResourceTab;
}

export const ARGO_TABS = ['apps', 'appsets'] as const;
export type ArgoTab = (typeof ARGO_TABS)[number];

export const ARGO_VIEWS = ['tiles', 'table'] as const;
export type ArgoView = (typeof ARGO_VIEWS)[number];

export const ARGO_APP_TABS = [
  'summary',
  'resources',
  'operation',
  'history',
] as const;
export type ArgoAppTab = (typeof ARGO_APP_TABS)[number];

export const ARGO_RESOURCE_TABS = [
  'summary',
  'diff',
  'live',
  'desired',
  'events',
  'logs',
] as const;
export type ArgoResourceTab = (typeof ARGO_RESOURCE_TABS)[number];

/** The value every filter uses for "no filter". */
export const ANY = 'all';

export const EMPTY_ARGO_URL_STATE: ArgoUrlState = {
  tab: 'apps',
  q: '',
  instance: ANY,
  sync: ANY,
  health: ANY,
  project: ANY,
  cluster: ANY,
  ns: ANY,
  autosync: ANY,
  labels: [],
  favoritesOnly: false,
  view: 'tiles',
  page: 1,
  app: '',
  appTab: 'summary',
  res: '',
  resTab: 'summary',
};

/** Moving between these is navigation; the filters merely refine a view. */
export const ARGO_NAVIGATION_KEYS = [
  'tab',
  'app',
  'appTab',
  'res',
  'resTab',
] as const satisfies readonly (keyof ArgoUrlState)[];

export function parseArgoUrl(params: URLSearchParams): ArgoUrlState {
  const page = Number(params.get('page'));
  return {
    tab: oneOf(params.get('tab'), ARGO_TABS, 'apps'),
    q: params.get('q') ?? '',
    instance: params.get('instance') || ANY,
    sync: params.get('sync') || ANY,
    health: params.get('health') || ANY,
    project: params.get('project') || ANY,
    cluster: params.get('cluster') || ANY,
    ns: params.get('ns') || ANY,
    autosync: params.get('autosync') || ANY,
    labels: (params.get('labels') ?? '').split(',').filter(Boolean),
    favoritesOnly: params.get('fav') === '1',
    view: oneOf(params.get('view'), ARGO_VIEWS, 'tiles'),
    page: Number.isInteger(page) && page > 0 ? page : 1,
    app: params.get('app') ?? '',
    appTab: oneOf(params.get('appTab'), ARGO_APP_TABS, 'summary'),
    res: params.get('res') ?? '',
    resTab: oneOf(params.get('resTab'), ARGO_RESOURCE_TABS, 'summary'),
  };
}

export function serializeArgoUrl(state: ArgoUrlState): URLSearchParams {
  const params = new URLSearchParams();
  setParam(params, 'tab', state.tab, 'apps');
  setParam(params, 'q', state.q);
  setParam(params, 'instance', state.instance, ANY);
  setParam(params, 'sync', state.sync, ANY);
  setParam(params, 'health', state.health, ANY);
  setParam(params, 'project', state.project, ANY);
  setParam(params, 'cluster', state.cluster, ANY);
  setParam(params, 'ns', state.ns, ANY);
  setParam(params, 'autosync', state.autosync, ANY);
  if (state.labels.length) params.set('labels', state.labels.join(','));
  if (state.favoritesOnly) params.set('fav', '1');
  setParam(params, 'view', state.view, 'tiles');
  if (state.page > 1) params.set('page', String(state.page));

  setParam(params, 'app', state.app);
  // A tab addresses nothing without the thing it is a tab of.
  if (state.app) {
    setParam(params, 'appTab', state.appTab, 'summary');
    setParam(params, 'res', state.res);
    if (state.res) setParam(params, 'resTab', state.resTab, 'summary');
  }
  return params;
}

/** `instance/namespace/name` — one parameter instead of three. */
export function appKey(
  instance: string,
  namespace: string,
  name: string,
): string {
  return [instance, namespace, name].join('/');
}

export function parseAppKey(
  value: string,
): { instance: string; namespace: string; name: string } | null {
  const parts = value.split('/');
  if (parts.length !== 3 || !parts[2]) return null;
  return { instance: parts[0], namespace: parts[1], name: parts[2] };
}

/** `group/kind/namespace/name`, matching upstream's nodeKey(). */
export function resourceKey(resource: {
  group?: string;
  kind?: string;
  namespace?: string;
  name?: string;
}): string {
  return [
    resource.group ?? '',
    resource.kind ?? '',
    resource.namespace ?? '',
    resource.name ?? '',
  ].join('/');
}
